import { Avatar, Button, CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import Chart from "chart.js/auto";
import axios from "axios";
import { API_URL } from "const/constants";
import { useEffect, useRef, useState } from "react";
import { AiFillEdit } from "react-icons/ai";
import {
  FaCheckCircle,
  FaClipboardList,
  FaComments,
  FaEnvelope,
  FaIdCard,
  FaPhone,
  FaShieldAlt,
  FaTimesCircle,
  FaUser,
  FaUsers,
} from "react-icons/fa";
import { IoArrowBack } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";

const ROLE_STYLES = {
  SUPER_ADMIN: "bg-rose-100 text-rose-700",
  ADMIN: "bg-blue-100 text-blue-700",
  ACCOUNT: "bg-amber-100 text-amber-700",
  USER: "bg-app-surface-muted text-app-text-secondary",
};

const RoleBadge = ({ role }) => {
  const style = ROLE_STYLES[role] || ROLE_STYLES.USER;
  return (
    <span
      className={`inline-flex rounded px-2 py-0.5 text-[11px] font-semibold uppercase ${style}`}
    >
      {(role || "—").replace(/_/g, " ")}
    </span>
  );
};

const DetailItem = ({ icon: Icon, label, children }) => (
  <div className="rounded-lg border border-app-border bg-app-surface p-4">
    <div className="flex items-start gap-3">
      {Icon && (
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-app-surface-muted text-app-text-secondary">
          <Icon className="h-4 w-4" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-app-text-muted">
          {label}
        </p>
        <div className="mt-1 text-sm font-medium text-app-text break-words">{children || "—"}</div>
      </div>
    </div>
  </div>
);

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-lg border border-app-border bg-app-surface px-4 py-4">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-app-text-muted">
          {label}
        </p>
        <p className="mt-1 text-2xl font-semibold text-app-text tabular-nums">{value ?? 0}</p>
        {sub ? <p className="mt-0.5 text-xs text-app-text-secondary">{sub}</p> : null}
      </div>
      {Icon && (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-app-surface-muted text-app-text-secondary">
          <Icon className="h-4 w-4" />
        </span>
      )}
    </div>
  </div>
);

const formatPermissionName = (name) =>
  name
    ?.replace(/_/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase())
    .trim();

const ViewUserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [clientStats] = useState([]);
  const [appStats] = useState([]);

  const clientChartRef = useRef(null);
  const appChartRef = useRef(null);
  const clientChartInstance = useRef(null);
  const appChartInstance = useRef(null);

  const { data: user, isFetching: isFetchingUserData } = useQuery({
    queryKey: ["userData", id],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/users/${id}`);
      return response.data.data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (!clientChartRef.current) return;
    if (clientChartInstance.current) clientChartInstance.current.destroy();

    clientChartInstance.current = new Chart(clientChartRef.current, {
      type: "bar",
      data: {
        labels: clientStats.map((stat) => stat.month),
        datasets: [
          {
            label: "Clients",
            data: clientStats.map((stat) => stat.count),
            backgroundColor: "#2563eb",
            borderRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { display: false } },
          y: { beginAtZero: true, ticks: { precision: 0 } },
        },
      },
    });
  }, [clientStats]);

  useEffect(() => {
    if (!appChartRef.current) return;
    if (appChartInstance.current) appChartInstance.current.destroy();

    appChartInstance.current = new Chart(appChartRef.current, {
      type: "bar",
      data: {
        labels: appStats.map((stat) => stat.month),
        datasets: [
          {
            label: "Applications",
            data: appStats.map((stat) => stat.count),
            backgroundColor: "#4f46e5",
            borderRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { display: false } },
          y: { beginAtZero: true, ticks: { precision: 0 } },
        },
      },
    });
  }, [appStats]);

  const userPermissionNames = (user?.permissions || []).map((perm) => perm?.name || perm);

  if (isFetchingUserData) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <CircularProgress size={28} />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center gap-4 px-4">
        <p className="text-base text-app-text-secondary">User not found</p>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Go back
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-app-border bg-app-surface text-app-text-secondary transition hover:bg-app-surface-muted"
            aria-label="Go back"
          >
            <IoArrowBack className="h-5 w-5" />
          </button>
          <div className="flex min-w-0 items-start gap-4">
            <Avatar
              src={user?.avatar}
              alt={user?.name}
              sx={{ width: 56, height: 56, fontSize: 24 }}
              className="border border-app-border bg-app-surface-muted"
            >
              <FaUser />
            </Avatar>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-app-text-muted">
                User profile
              </p>
              <h1 className="truncate text-xl font-semibold text-app-text sm:text-2xl">
                {user?.name || "User"}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <RoleBadge role={user?.type} />
                {user?.username && (
                  <span className="inline-flex items-center gap-1 text-xs text-app-text-secondary">
                    <FaIdCard className="h-3 w-3" />
                    {user.username}
                  </span>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-app-text-secondary">
                {user?.email && (
                  <span className="inline-flex items-center gap-1.5 min-w-0">
                    <FaEnvelope className="h-3.5 w-3.5 shrink-0 opacity-70" />
                    <span className="truncate">{user.email}</span>
                  </span>
                )}
                {user?.mobileNumber && (
                  <span className="inline-flex items-center gap-1.5">
                    <FaPhone className="h-3.5 w-3.5 shrink-0 opacity-70" />
                    {user.mobileNumber}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        <Button
          variant="outlined"
          startIcon={<AiFillEdit />}
          onClick={() => navigate(`/admin/user/edit/${id}`, { state: { item: user } })}
          className="shrink-0 self-start sm:self-auto"
        >
          Edit user
        </Button>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          icon={FaUsers}
          label="Total clients"
          value={user?.totalClients || 0}
          sub="Assigned to this user"
        />
        <StatCard
          icon={FaClipboardList}
          label="Total applications"
          value={user?.totalApplications || 0}
          sub="All time"
        />
        <StatCard
          icon={FaUsers}
          label="This month"
          value={user?.thisMonthClients || 0}
          sub="Clients added this month"
        />
      </div>

      {/* Details + permissions */}
      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="overflow-hidden rounded-xl border border-app-border bg-app-surface shadow-sm">
          <div className="border-b border-app-border px-4 py-3 sm:px-6">
            <h2 className="text-sm font-semibold text-app-text">User details</h2>
            <p className="text-xs text-app-text-muted">Account and contact information</p>
          </div>
          <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 sm:p-6">
            <DetailItem icon={FaUser} label="Full name">
              {user?.name}
            </DetailItem>
            <DetailItem icon={FaIdCard} label="Username">
              {user?.username}
            </DetailItem>
            <DetailItem icon={FaEnvelope} label="Email">
              {user?.email ? (
                <a href={`mailto:${user.email}`} className="text-primary hover:underline">
                  {user.email}
                </a>
              ) : null}
            </DetailItem>
            <DetailItem icon={FaPhone} label="Mobile number">
              {user?.mobileNumber}
            </DetailItem>
            <DetailItem icon={FaUser} label="User type">
              <RoleBadge role={user?.type} />
            </DetailItem>
            <DetailItem icon={FaComments} label="Discussion access">
              <span className="inline-flex items-center gap-1.5">
                {user?.accessToDiscussion ? (
                  <>
                    <FaCheckCircle className="text-emerald-600" />
                    <span className="text-emerald-700">Yes</span>
                  </>
                ) : (
                  <>
                    <FaTimesCircle className="text-rose-500" />
                    <span className="text-rose-600">No</span>
                  </>
                )}
              </span>
            </DetailItem>
            {user?.organization?.name && (
              <DetailItem icon={FaUsers} label="Organization">
                {user.organization.id ? (
                  <button
                    type="button"
                    className="text-left text-primary hover:underline"
                    onClick={() =>
                      navigate(`/admin/consultancy/view/${user.organization.id}`, {
                        state: { item: user.organization },
                      })
                    }
                  >
                    {user.organization.name}
                  </button>
                ) : (
                  user.organization.name
                )}
              </DetailItem>
            )}
          </div>
        </section>

        <section className="overflow-hidden rounded-xl border border-app-border bg-app-surface shadow-sm">
          <div className="border-b border-app-border px-4 py-3 sm:px-6">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-app-text">
              <FaShieldAlt className="h-4 w-4 text-app-text-muted" />
              Permissions
            </h2>
            <p className="text-xs text-app-text-muted">Module access for this user</p>
          </div>
          <div className="p-4 sm:p-6">
            {user?.type === "SUPER_ADMIN" ? (
              <div className="rounded-lg border border-app-border bg-app-surface-muted px-4 py-3">
                <p className="text-sm font-medium text-app-text">
                  Super admin — has access to all modules
                </p>
              </div>
            ) : userPermissionNames.length > 0 ? (
              <div>
                <p className="mb-3 text-sm text-app-text-secondary">
                  This user has access to the following modules:
                </p>
                <div className="flex flex-wrap gap-2">
                  {userPermissionNames.map((permission, index) => (
                    <span
                      key={index}
                      className="inline-flex rounded border border-app-border bg-app-surface-muted px-2.5 py-1 text-xs font-medium text-app-text-secondary"
                    >
                      {formatPermissionName(permission)}
                    </span>
                  ))}
                </div>
                <p className="mt-4 text-xs text-app-text-muted">
                  Total: {userPermissionNames.length} permission
                  {userPermissionNames.length !== 1 ? "s" : ""}
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <FaShieldAlt className="mb-3 text-3xl text-app-text-muted/50" />
                <p className="text-sm font-medium text-app-text-secondary">
                  No permissions assigned
                </p>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <section className="overflow-hidden rounded-xl border border-app-border bg-app-surface shadow-sm">
          <div className="border-b border-app-border px-4 py-3 sm:px-6">
            <h2 className="text-sm font-semibold text-app-text">Clients added per month</h2>
          </div>
          <div className="relative h-[260px] p-4 sm:p-6">
            {clientStats.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-app-text-muted">
                No client activity data available
              </div>
            ) : (
              <canvas ref={clientChartRef} />
            )}
          </div>
        </section>

        <section className="overflow-hidden rounded-xl border border-app-border bg-app-surface shadow-sm">
          <div className="border-b border-app-border px-4 py-3 sm:px-6">
            <h2 className="text-sm font-semibold text-app-text">Applications per month</h2>
          </div>
          <div className="relative h-[260px] p-4 sm:p-6">
            {appStats.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-app-text-muted">
                No application activity data available
              </div>
            ) : (
              <canvas ref={appChartRef} />
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ViewUserDetail;
